import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { applyChangesToValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { EntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedMetrics } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { determineFilterStoreScope } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getPrimaryFilterFullKey } from 'libs/end-to-end/src/filters-v2/filter-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { FilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FocusArea } from 'libs/end-to-end/src/focus-area';

export const MetricMultipleMenu = ({
  store,
  feature,
  databaseRevisionId,
  databaseId,
  organizationId,
  setPrimaryFilters,
  closeMenu,
  entitledFilterFeatureDetails,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  closeMenu: () => void;
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
}) => {
  const language = useLanguage();

  const [selectedMetrics, setSelectedMetrics] = useState<string[] | null>(null);

  const metrics = useMemo(() => {
    return (
      entitledFilterFeatureDetails.focusAreasWithMetrics.flatMap(
        (focusArea) => focusArea.metrics,
      ) ?? []
    );
  }, [entitledFilterFeatureDetails.focusAreasWithMetrics]);

  const currentSelectedMetrics = useMemo(() => {
    return (
      getSelectedMetrics({
        feature,
        organizationId,
        databaseRevisionId,
        databaseId,
        store,
      }) ?? []
    );
  }, [feature, organizationId, databaseRevisionId, databaseId, store]);

  useEffect(() => {
    if (currentSelectedMetrics.length > 0 && selectedMetrics === null) {
      setSelectedMetrics(currentSelectedMetrics);
    }
  }, [selectedMetrics, currentSelectedMetrics]);

  const items = useMemo(() => {
    const fullKey = getPrimaryFilterFullKey({
      primaryFilterKey: 'metrics',
    });

    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return [];
    }

    return metrics.map((metric) => {
      let translatedContent = language.metricDisplayName({
        definition: metric,
      });

      if (feature === 'geolocation' && metric.whiteLabeling.eventDisplayName) {
        translatedContent = metric.whiteLabeling.eventDisplayName;
      }

      return {
        value: metric.columnHeaderValue,
        translatedContent,
        selected: selectedMetrics?.includes(metric.columnHeaderValue) ?? false,
      };
    });
  }, [
    feature,
    databaseId,
    databaseRevisionId,
    organizationId,
    metrics,
    language,
    selectedMetrics,
    focusArea,
    analysisName,
  ]);

  return (
    <FilterMenuSinglePanel
      filterContent={
        <FilterListBox
          multiple={true}
          items={items}
          onUpdate={({ addedSelections, removedSelections }) => {
            const newSelections = applyChangesToValues({
              removedSelections,
              addedSelections,
              selections: selectedMetrics ?? [],
            });

            setSelectedMetrics(newSelections);
          }}
        />
      }
      bottomProps={{
        onApply: () => {
          const primaryFiltersSelections: PrimaryFilterSelection[] = [
            {
              primaryFilterKey: 'metrics',
              value: selectedMetrics ?? [],
            },
          ];
          setPrimaryFilters({ selections: primaryFiltersSelections });
          closeMenu();
        },
        onCancel: () => {
          closeMenu();
        },
        applyDisabled: selectedMetrics === null || selectedMetrics.length === 0,
        errorTranslatedContent:
          selectedMetrics === null || selectedMetrics.length === 0
            ? language.pleaseSelect({
                fieldDisplayNames: [language.metric()],
              })
            : null,
      }}
    />
  );
};
